import { useState } from "react";
import { itemLine, itemName, itemPictures, realTitle } from "../lib/identityDecision";

// "Barcode already on file": the sheet that asks which existing item the barcode
// belongs to. One candidate gets a swipeable picture (pack shot first, then the
// merchant's photos); several get radio rows. The camera stays behind, dimmed,
// and a tap on it does nothing: the × is the way out.
//
// Injected like the camera's CSS (dangerouslySetInnerHTML), for the same hydration reason.

const CSS = `
.bof{
  position:absolute; inset:0; z-index:9; background:rgba(0,0,0,.35); color:#f4f2ee;
  display:flex; flex-direction:column; justify-content:flex-end; font-family:system-ui,sans-serif;
}
.bof-bar{
  position:absolute; z-index:1; left:0; right:0; top:0; box-sizing:border-box;
  padding:calc(env(safe-area-inset-top,0px) + 16px) 16px 28px;
  background:linear-gradient(to bottom,rgba(0,0,0,.6),rgba(0,0,0,0));
}
.bof-bar .cap-sbar{max-width:560px; margin:0 auto;}
.bof-pill{
  position:absolute; z-index:1; left:0; right:0; top:calc(env(safe-area-inset-top,0px) + 72px);
  display:flex; justify-content:center; pointer-events:none;
}
.bof-pill span{
  display:inline-flex; align-items:center; gap:6px; height:30px; padding:0 12px; border-radius:15px;
  background:rgba(0,0,0,.6); font-size:13px; font-weight:500; font-variant-numeric:tabular-nums;
}
.bof-pill i{color:#7fd69a; display:inline-flex;}
.bof-sheet{
  position:relative; z-index:2; box-sizing:border-box; width:100%; max-width:560px; margin:0 auto;
  max-height:calc(100% - 120px); display:flex; flex-direction:column; gap:14px;
  padding:10px 20px calc(env(safe-area-inset-bottom,0px) + 28px);
  background:#161615; border-radius:24px 24px 0 0;
  animation:bof-up .2s ease-out;
}
@keyframes bof-up{from{transform:translateY(100%);}to{transform:none;}}
.bof-grab{align-self:center; flex:none; width:36px; height:4px; border-radius:2px; background:#3a3936;}
.bof-snap{
  flex:none; display:flex; height:230px; border-radius:18px; overflow-x:auto; overflow-y:hidden;
  scroll-snap-type:x mandatory; scrollbar-width:none; overscroll-behavior-x:contain;
}
.bof-snap::-webkit-scrollbar{display:none;}
.bof-slide{position:relative; flex:0 0 100%; scroll-snap-align:start; background:#222120;}
.bof-slide img{width:100%; height:100%; object-fit:cover; display:block;}
.bof-slide--pack{background:linear-gradient(180deg,#fff,#f3f1ec);}
.bof-slide--pack img{object-fit:contain;}
.bof-tag{
  position:absolute; left:10px; top:10px; height:24px; padding:0 9px; border-radius:12px;
  background:rgba(0,0,0,.6); color:#fff; font-size:11px; font-weight:600; display:inline-flex; align-items:center;
}
.bof-none{
  flex:none; height:230px; box-sizing:border-box; border-radius:18px; border:1.5px dashed #4a4945;
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px;
  color:#a8a39a; font-size:14px;
}
.bof-dots{flex:none; display:flex; gap:6px; justify-content:center; margin-top:-6px;}
.bof-dots i{width:6px; height:6px; border-radius:3px; background:rgba(255,255,255,.3);}
.bof-dots i.on{width:16px; background:#fff;}
.bof-head{flex:none; display:flex; flex-direction:column; gap:2px; min-width:0;}
.bof-name{
  font-size:18px; line-height:24px; font-weight:650; overflow:hidden; overflow-wrap:anywhere;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;
}
.bof-sub{font-size:13px; color:#a8a39a;}
.bof-list{flex:1 1 auto; min-height:0; overflow-y:auto; display:flex; flex-direction:column; gap:8px;}
.bof-row{
  appearance:none; border:0; font:inherit; color:inherit; text-align:left; cursor:pointer; flex:none;
  display:flex; align-items:center; gap:14px; padding:10px; border-radius:18px;
  background:rgba(255,255,255,.05); -webkit-tap-highlight-color:transparent;
}
.bof-row--on{background:rgba(242,176,30,.08); box-shadow:inset 0 0 0 1.5px #f2b01e;}
.bof-pic{
  position:relative; flex:none; width:72px; height:72px; border-radius:12px; overflow:hidden;
  background:#222120; box-sizing:border-box;
}
.bof-pic img{width:100%; height:100%; object-fit:cover; display:block;}
.bof-pic--pack{background:linear-gradient(180deg,#fff,#f3f1ec);}
.bof-pic--pack img{object-fit:contain;}
.bof-pic--none{
  border:1.5px dashed #4a4945; display:flex; align-items:center; justify-content:center;
  color:#a8a39a; font-size:10px;
}
.bof-txt{flex:1; min-width:0; display:flex; flex-direction:column; gap:2px;}
.bof-txt b{font-size:16px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.bof-txt span{font-size:12px; color:#a8a39a; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.bof-radio{
  flex:none; width:22px; height:22px; border-radius:11px; box-sizing:border-box; border:2px solid #55534e;
  display:inline-flex; align-items:center; justify-content:center;
}
.bof-row--on .bof-radio{border-color:#f2b01e;}
.bof-radio i{width:10px; height:10px; border-radius:5px; background:#f2b01e;}
.bof-go{
  appearance:none; border:0; font:inherit; flex:none; box-sizing:border-box; height:52px; border-radius:26px;
  padding:0 20px; background:#f2b01e; color:#1b1a18; font-size:15px; font-weight:700; cursor:pointer;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; -webkit-tap-highlight-color:transparent;
}
.bof-go:disabled{background:#3a3936; color:#8a857c; cursor:default;}
.bof-go:active:not(:disabled){transform:scale(.98);}
.bof-alt{
  appearance:none; border:0; background:none; font:inherit; flex:none; align-self:center;
  height:44px; padding:0 12px; font-size:14px; color:#d9d5cd; cursor:pointer;
}
@media (prefers-reduced-motion:reduce){ .bof-sheet{animation:none;} .bof-go:active:not(:disabled){transform:none;} }
`;

// One candidate: its pictures in a swipeable strip, dots under it when there are several.
function Pictures({ c }) {
  const pics = itemPictures(c);
  const [at, setAt] = useState(0);
  if (!pics.length) {
    return (
      <div className="bof-none">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.2" /></svg>
        No photos yet
      </div>
    );
  }
  return (
    <>
      <div className="bof-snap" onScroll={(e) => setAt(Math.round(e.currentTarget.scrollLeft / (e.currentTarget.clientWidth || 1)))}>
        {pics.map((p, i) => (
          <div key={`${p.url}-${i}`} className={"bof-slide" + (p.kind === "packshot" ? " bof-slide--pack" : "")}>
            <img src={p.url} alt="" draggable={false} decoding="async" loading={i === 0 ? "eager" : "lazy"} fetchpriority={i === 0 ? "high" : "low"} />
            {p.kind === "packshot" ? <span className="bof-tag">Pack shot</span> : p.kind === "photo" ? <span className="bof-tag">Your photo</span> : <span className="bof-tag">Shopify</span>}
          </div>
        ))}
      </div>
      {pics.length > 1 ? (
        <div className="bof-dots" aria-hidden="true">
          {pics.map((_, i) => <i key={i} className={i === at ? "on" : ""} />)}
        </div>
      ) : null}
    </>
  );
}

// A several-candidate row's 72px picture: pack shot, else the first photo, else a dashed "No photo".
function RowPic({ c }) {
  const pic = itemPictures(c)[0];
  if (!pic) return <div className="bof-pic bof-pic--none">No photo</div>;
  return (
    <div className={"bof-pic" + (pic.kind === "packshot" ? " bof-pic--pack" : "")}>
      <img src={pic.url} alt="" draggable={false} decoding="async" />
    </div>
  );
}

/**
 * ident: the "decide" identity ({barcode, candidates, sel}). onSel(i) picks a row,
 * onPick(c) files against an item, onNew() is "a different item", onClose() is the ×.
 */
export default function BarcodeOnFileSheet({ ident, quickMode, onSel, onPick, onNew, onClose }) {
  const cands = ident.candidates;
  const one = cands.length === 1;
  const c = one ? cands[0] : cands[ident.sel];
  const none = one && !itemPictures(c).length && !c.photoCount;
  return (
    <div className="bof" role="dialog" aria-modal="true" aria-label="Barcode already on file">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="bof-bar">
        <div className="cap-sbar">
          <button type="button" className="cap-sbtn" aria-label="Close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <div className="cap-stitle">
            <span>{quickMode ? "Quick scan" : "Bulk scan"}</span>
            <span>Barcode read</span>
          </div>
          <span />
        </div>
      </div>
      <div className="bof-pill">
        <span>
          <i><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg></i>
          {ident.barcode}
        </span>
      </div>
      <div className="bof-sheet">
        <span className="bof-grab" />
        {one ? (
          <>
            <Pictures c={c} />
            <div className="bof-head">
              <span className="bof-name">{itemName(c)}</span>
              <span className="bof-sub">{itemLine(c)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="bof-head">
              <span className="bof-name">{cands.length} items have this code</span>
              <span className="bof-sub">Pick the one in your hand</span>
            </div>
            <div className="bof-list" role="radiogroup" aria-label="Barcode already on file">
              {cands.map((k, i) => (
                <button
                  key={`${k.productId}-${k.variantId || i}`}
                  type="button"
                  role="radio"
                  aria-checked={ident.sel === i}
                  className={"bof-row" + (ident.sel === i ? " bof-row--on" : "")}
                  onClick={() => onSel(i)}
                >
                  <RowPic c={k} />
                  <span className="bof-txt">
                    <b>{itemName(k)}</b>
                    <span>{itemLine(k, k.variantTitle || (realTitle(k) ? "" : undefined))}</span>
                  </span>
                  <span className="bof-radio">{ident.sel === i ? <i /> : null}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <button type="button" className="bof-go" disabled={!c} onClick={() => c && onPick(c)}>
          {one ? (none ? "Take its first photos" : "Add photos to this item") : `Add photos to ${c ? itemName(c) : "this item"}`}
        </button>
        <button type="button" className="bof-alt" onClick={onNew}>
          {one ? "It’s a different item" : "None of these · new item"}
        </button>
      </div>
    </div>
  );
}
